import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Handshake, Microscope } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import WorldMap from "@/components/WorldMap";

const PERSONAS = [
  {
    icon: Building2,
    label: "Series A · $9.5M Phase I Raise",
    body: "We are currently executing a $9.5M Phase I Series A capital raise for the construction of our 1,250 m² GMP Clean Core and initial flowering canopy. This is an asset-backed deployment into a shovel-ready, licensed pharmaceutical infrastructure project.",
    points: ["Commercial licence · secured", "1,250 m² EU-GMP Clean Core", "Shovel-ready · asset-backed"],
  },
  {
    icon: Handshake,
    label: "Pharmaceutical Importers",
    body: "Establishing early forward-purchase and capacity allocations for our Phase I production cycles. Our facility is engineered to provide the supply chain stability and batch homogeneity required for the German, UK, and Australian markets.",
    points: ["Forward-purchase allocation", "Batch homogeneity by design", "DE · UK · AU markets"],
  },
  {
    icon: Microscope,
    label: "Technical Partners",
    body: "Agronomy, engineering, GMP, analytical-method, and logistics collaborators across the value chain.",
    points: ["Cultivation science", "Processing engineering", "Analytical & QA collaboration"],
  },
];

export default function Investors() {
  return (
    <>
      <section className="pt-[72px] bg-bone">
        <div className="relative overflow-hidden bg-forest">
          <img
            src="/Images/investors-hero-facility.jpg"
            alt="Pharmaceutical-grade facility infrastructure"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-forest/80 via-forest/55 to-forest/85" />
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 pt-24 md:pt-36 pb-24 md:pb-36 min-h-[560px] md:min-h-[680px] flex flex-col justify-end">
            <SectionReveal><div className="text-copper text-[11px] tracking-[0.22em] uppercase font-semibold">Investors & Partners</div></SectionReveal>
            <SectionReveal delay={120}>
              <h1 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[64px] leading-[1.04] tracking-editorial max-w-5xl text-bone">
                A strategic position at the intersection of African agriculture and European pharma.
              </h1>
            </SectionReveal>
            <SectionReveal delay={240}>
              <p className="mt-8 max-w-2xl text-bone/75 text-lg leading-relaxed">
                We are speaking with a focused group of capital, importer, and technical partners. Our platform is engineered to scale with the regulated demand we are entering.
              </p>
            </SectionReveal>
          </div>
        </div>
      </section>

      <section className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <SectionReveal>
            <div className="overline">Strategic Positioning</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial max-w-3xl">
              Three partner profiles. One disciplined platform.
            </h2>
          </SectionReveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
            {PERSONAS.map((p, i) => (
              <SectionReveal key={p.label} delay={i * 100} className="h-full">
                <div data-testid={`partner-${i}`} className="bg-sand/50 p-8 md:p-10 h-full">
                  <div className="flex items-center justify-between">
                    <p.icon size={20} strokeWidth={1.5} className="text-copper" />
                    <div className="font-mono text-[11px] tracking-[0.2em] text-copper">P/0{i + 1}</div>
                  </div>
                  <h3 className="mt-8 font-display text-2xl tracking-tight">{p.label}</h3>
                  <p className="mt-3 text-sm text-ink2 leading-relaxed">{p.body}</p>
                  <ul className="mt-6 space-y-2">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-3 text-sm text-ink">
                        <span className="h-px w-5 bg-copper" />{pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand/60 border-y border-line/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <SectionReveal className="lg:col-span-5">
            <div className="overline">Export Positioning</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial">
              Origin in Uganda. Destination across regulated international markets.
            </h2>
            <p className="mt-6 text-ink2 max-w-md leading-relaxed">
              Our export programme is structured around established and emerging medicinal cannabis import jurisdictions — focused on long-term, institutional supply relationships.
            </p>
          </SectionReveal>
          <SectionReveal className="lg:col-span-7" delay={120}>
            <WorldMap />
          </SectionReveal>
        </div>
      </section>

      <section className="bg-forest text-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <SectionReveal>
            <div className="text-copper text-[11px] tracking-[0.22em] uppercase font-semibold">Inquiries</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-[52px] leading-[1.05] tracking-editorial max-w-3xl text-bone">
              For investor relations, partnership opportunities, and importer programmes.
            </h2>
          </SectionReveal>
          <SectionReveal delay={120}>
            <Link
              to="/contact"
              data-testid="investors-cta-contact"
              className="inline-flex items-center gap-2 bg-bone text-ink px-7 py-4 text-sm tracking-wide hover:bg-sand transition-colors"
            >
              Open inquiry form <ArrowUpRight size={16} />
            </Link>
          </SectionReveal>
        </div>
      </section>
    </>
  );
}
